import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/MainLayout";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { CardLoader, CablewayLoader, PageLoader } from "@/components/MoneyMachineLoader";
import { RiskBadge } from "@/components/RiskBadge";
import { OnboardingPanel } from "@/components/OnboardingPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  CreditCard,
  Shield,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Bot,
  Send,
  User,
  BarChart3,
  Loader2,
  HeartPulse,
  Lightbulb,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import ReactMarkdown from "react-markdown";
import { InfoTip } from "@/components/InfoTip";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Returns Tailwind indicator color class based on a 0-100 score
// "health" mode: high = green, low = red (for health/diversification/credit)
// "risk" mode: low = green, high = red (for risk scores)
const scoreColor = (score, mode = "health") => {
  const s = mode === "risk" ? 100 - score : score;
  if (s >= 70) return "bg-green-500";
  if (s >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const Dashboard = () => {
  const { summary, creditCapacity, assets, credits, loading, pricesRefreshing, refreshPrices, formatCurrency } = usePortfolio();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [isAdvisorOpen, setIsAdvisorOpen] = useState(false);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const chatEndRef = useRef(null);

  // Historical chart state
  const [historyRange, setHistoryRange] = useState("month");
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Health score state
  const [healthScore, setHealthScore] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  
  // Check if user has no assets
  const hasNoAssets = !assets || assets.length === 0;

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Fetch portfolio history when range changes
  useEffect(() => {
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const resp = await axios.get(`${API_URL}/api/portfolio/history?range=${historyRange}`, { withCredentials: true });
        const raw = resp.data?.history || [];
        setHistoryData(raw.map((d) => ({
          date: d.date,
          value: d.total_value,
          netWorth: d.net_worth,
          label: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
        })));
      } catch {
        setHistoryData([]);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [historyRange]);

  // Fetch health score
  useEffect(() => {
    const fetchHealth = async () => {
      setHealthLoading(true);
      try {
        const resp = await axios.get(`${API_URL}/api/portfolio/health-score`, { withCredentials: true });
        setHealthScore(resp.data);
      } catch {
        setHealthScore(null);
      } finally {
        setHealthLoading(false);
      }
    };
    if (!loading) fetchHealth();
  }, [loading]);

  const startAdvisorChat = async () => {
    setIsAdvisorOpen(true);
    if (chatMessages.length === 0) {
      // Initial analysis
      setAdvisorLoading(true);
      setChatMessages([{ role: "assistant", content: "Analyzing your portfolio...", loading: true }]);
      
      try {
        const response = await axios.post(
          `${API_URL}/api/ai/wealth-advisor`,
          {
            action: "initial_analysis",
            portfolio_summary: summary,
            assets: assets,
            credits: credits,
            credit_capacity: creditCapacity
          },
          { withCredentials: true, timeout: 120000 }
        );
        
        setChatMessages([{ role: "assistant", content: response.data.message }]);
      } catch (error) {
        setChatMessages([{ 
          role: "assistant", 
          content: "I apologize, but I'm having trouble analyzing your portfolio right now. Please try again later." 
        }]);
      } finally {
        setAdvisorLoading(false);
      }
    }
  };

  const sendMessage = async () => {
    if (!userInput.trim() || advisorLoading) return;
    
    const newMessage = { role: "user", content: userInput };
    setChatMessages(prev => [...prev, newMessage]);
    setUserInput("");
    setAdvisorLoading(true);
    
    // Add loading message
    setChatMessages(prev => [...prev, { role: "assistant", content: "Thinking...", loading: true }]);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/wealth-advisor`,
        {
          action: "chat",
          message: userInput,
          portfolio_summary: summary,
          assets: assets,
          credits: credits,
          credit_capacity: creditCapacity,
          chat_history: chatMessages.filter(m => !m.loading)
        },
        { withCredentials: true, timeout: 120000 }
      );
      
      // Remove loading message and add real response
      setChatMessages(prev => [...prev.filter(m => !m.loading), { role: "assistant", content: response.data.message }]);
    } catch (error) {
      setChatMessages(prev => [...prev.filter(m => !m.loading), { 
        role: "assistant", 
        content: "I apologize, but I couldn't process your question. Please try again." 
      }]);
    } finally {
      setAdvisorLoading(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  const pieData = summary?.asset_breakdown
    ? Object.entries(summary.asset_breakdown)
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
    : [];
  const pieTotal = pieData.reduce((s, d) => s + d.value, 0) || 1;

  const COLORS = ["hsl(76, 91%, 60%)", "hsl(217, 91%, 60%)", "hsl(43, 74%, 66%)", "hsl(160, 60%, 45%)", "hsl(280, 65%, 60%)"];

  // Use real history data for the mini chart, or fallback estimate
  const miniChartData = historyData.length > 1
    ? historyData
    : [
        { label: "Start", value: (summary?.total_cost || summary?.total_value * 0.9 || 0) },
        { label: "Now", value: summary?.total_value || 0 },
      ];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Onboarding Panel - shown when no assets */}
        {hasNoAssets && (
          <OnboardingPanel 
            compact={true}
            onOpenAI={() => navigate("/assets")}
            onOpenManual={() => navigate("/assets")}
          />
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
              {t("dash.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("dash.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Only show AI Advisor button if user has assets */}
            {!hasNoAssets && (
              <Button 
                onClick={startAdvisorChat}
                data-testid="ai-advisor-btn"
                className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-bold uppercase tracking-wider text-xs glow-primary gap-2"
              >
                <Bot className="w-4 h-4" />
                {t("dash.advisor")}
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={refreshPrices}
              disabled={pricesRefreshing}
              data-testid="refresh-prices-btn"
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${pricesRefreshing ? "animate-spin" : ""}`} />
              {pricesRefreshing ? t("dash.updating") : t("dash.refreshPrices")}
            </Button>
          </div>
        </div>

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Total Portfolio Value - Large Card */}
          <Card className="md:col-span-8 bg-card border-border overflow-hidden relative group" data-testid="total-value-card">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-primary/5 blur-[100px] rounded-full pointer-events-none" />
            <CardContent className="p-8 relative">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80 mb-2">
                    {t("dash.totalValue")}<InfoTip text="Sum of current market value of all your assets (quantity × current price), across all asset types." />
                  </p>
                  <p className="text-5xl md:text-6xl font-mono font-bold tracking-tighter text-foreground">
                    {formatCurrency(summary?.total_value || 0)}
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    {summary?.gain_loss_percentage >= 0 ? (
                      <div className="flex items-center gap-1 text-green-500">
                        <ArrowUpRight className="w-5 h-5" />
                        <span className="text-lg font-semibold">+{summary?.gain_loss_percentage?.toFixed(1)}%</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-red-500">
                        <ArrowDownRight className="w-5 h-5" />
                        <span className="text-lg font-semibold">{summary?.gain_loss_percentage?.toFixed(1)}%</span>
                      </div>
                    )}
                    <span className="text-sm text-muted-foreground">{t("dash.allTime")}</span>
                    {summary?.annualized_return !== null && summary?.annualized_return !== undefined && (
                      <span className={`text-sm font-mono font-semibold ml-2 ${summary.annualized_return >= 0 ? "text-green-500" : "text-red-500"}`}>
                        ({summary.annualized_return >= 0 ? "+" : ""}{summary.annualized_return.toFixed(1)}% ann.)
                      </span>
                    )}
                    {summary?.avg_holding_days != null && (
                      <span className="text-xs text-muted-foreground ml-2">
                        {summary.avg_holding_days >= 365
                          ? `${(summary.avg_holding_days / 365).toFixed(1)}y held`
                          : summary.avg_holding_days >= 30
                            ? `${Math.floor(summary.avg_holding_days / 30)}mo held`
                            : `${summary.avg_holding_days}d held`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Wallet className="w-7 h-7 text-primary" />
                </div>
              </div>
              
              <div className="mt-6 h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={miniChartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(76, 91%, 60%)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(76, 91%, 60%)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(76, 91%, 60%)"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorValue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Net Worth & Risk Score */}
          <div className="md:col-span-4 space-y-6">
            <Card className="bg-card border-border" data-testid="net-worth-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                    {t("dash.netWorth")}<InfoTip text="Total portfolio value minus all outstanding credit/debt. Reflects your true financial position." />
                  </p>
                  <DollarSign className="w-5 h-5 text-primary" />
                </div>
                <p className="text-3xl font-mono font-bold">
                  {formatCurrency(summary?.net_worth || 0)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  After {summary?.credit_count || 0} credits
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="risk-score-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                    {t("dash.riskScore")}<InfoTip text="Value-weighted average of individual asset risk scores (0-100). Higher = riskier. Weighted by each asset's share of total portfolio." />
                  </p>
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-3xl font-mono font-bold">
                    {summary?.average_risk_score || 50}
                  </p>
                  <RiskBadge score={summary?.average_risk_score || 50} />
                </div>
                <Progress 
                  value={summary?.average_risk_score || 50} 
                  className="mt-3 h-2"
                  indicatorClassName={scoreColor(summary?.average_risk_score || 50, "risk")}
                />
              </CardContent>
            </Card>
          </div>

          {/* Portfolio Health Score */}
          <Card className="md:col-span-12 bg-card border-border overflow-hidden relative" data-testid="health-score-card">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
            <CardContent className="p-6 relative">
              {healthLoading ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : healthScore ? (
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                  {/* Grade Circle */}
                  <div className="flex items-center gap-5 shrink-0">
                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 ${
                      healthScore.grade === "A" ? "bg-green-500/10 border-green-500/30 text-green-500" :
                      healthScore.grade === "B" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500" :
                      healthScore.grade === "C" ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-500" :
                      healthScore.grade === "D" ? "bg-orange-500/10 border-orange-500/30 text-orange-500" :
                      "bg-red-500/10 border-red-500/30 text-red-500"
                    }`}>
                      <span className="text-4xl font-black font-mono" data-testid="health-grade">{healthScore.grade}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <HeartPulse className="w-4 h-4 text-primary" />
                        <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">Portfolio Health<InfoTip text="Combined score (A-F) weighing Diversification (40%), Risk Balance (30%), and Credit Health (30%). Grades: A≥85, B≥70, C≥55, D≥40, F<40." /></p>
                      </div>
                      <p className="text-2xl font-mono font-bold" data-testid="health-score-value">{healthScore.score}<span className="text-sm text-muted-foreground font-normal">/100</span></p>
                    </div>
                  </div>

                  {/* Factor Bars */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-muted-foreground">Diversification</p>
                        <span className="text-xs font-mono font-bold">{healthScore.diversification.score}</span>
                      </div>
                      <Progress value={healthScore.diversification.score} className="h-2" indicatorClassName={scoreColor(healthScore.diversification.score)} data-testid="health-div-bar" />
                      <p className="text-[10px] text-muted-foreground">{healthScore.diversification.detail}</p>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-muted-foreground">Risk Balance</p>
                        <span className="text-xs font-mono font-bold">{healthScore.risk.score}</span>
                      </div>
                      <Progress value={healthScore.risk.score} className="h-2" indicatorClassName={scoreColor(healthScore.risk.score)} data-testid="health-risk-bar" />
                      <p className="text-[10px] text-muted-foreground">{healthScore.risk.detail}</p>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-muted-foreground">Credit Health</p>
                        <span className="text-xs font-mono font-bold">{healthScore.credit_health.score}</span>
                      </div>
                      <Progress value={healthScore.credit_health.score} className="h-2" indicatorClassName={scoreColor(healthScore.credit_health.score)} data-testid="health-credit-bar" />
                      <p className="text-[10px] text-muted-foreground">{healthScore.credit_health.detail}</p>
                    </div>
                  </div>

                  {/* Tip */}
                  {healthScore.tips?.[0] && (
                    <div className="shrink-0 max-w-xs p-3 bg-primary/5 border border-primary/10 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="health-tip">{healthScore.tips[0]}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Asset Breakdown */}
          <Card className="md:col-span-4" data-testid="asset-breakdown-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80">
                {t("dash.breakdown")}<InfoTip text="Portfolio allocation by asset type — shows how your total value is distributed." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <div className="flex items-center gap-6">
                  <div className="w-32 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={35}
                          outerRadius={55}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2">
                    {pieData.map((entry, index) => (
                      <div key={entry.name} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{entry.name}</span>
                        <span className="text-xs font-mono text-muted-foreground ml-auto">
                          {((entry.value / pieTotal) * 100).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm py-8 text-center">
                  {t("dash.noAssets")}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Credit Capacity */}
          <Card className="md:col-span-4" data-testid="credit-capacity-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80">
                {t("dash.creditCapacity")}<InfoTip text="Maximum borrowing power based on LTV ratios of your assets. Available = total capacity minus used credit." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{t("dash.available")}</span>
                    <span className="font-mono font-semibold text-primary">
                      {formatCurrency(creditCapacity?.available_credit || 0)}
                    </span>
                  </div>
                  <Progress 
                    value={100 - (creditCapacity?.utilization_rate || 0)} 
                    className="h-2"
                    indicatorClassName={scoreColor(100 - (creditCapacity?.utilization_rate || 0))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("dash.totalCapacity")}</p>
                    <p className="font-mono font-semibold">
                      {formatCurrency(creditCapacity?.total_credit_capacity || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("dash.used")}</p>
                    <p className="font-mono font-semibold">
                      {formatCurrency(creditCapacity?.used_credit || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Overview */}
          <Card className="md:col-span-4" data-testid="monthly-stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80">
                {t("dash.performance")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm">{t("dash.pl")}<InfoTip text="Total unrealized profit/loss: (current value - cost basis) summed across all assets." /></span>
                  </div>
                  <span className={`font-mono font-semibold ${(summary?.gain_loss || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {(summary?.gain_loss || 0) >= 0 ? "+" : ""}{formatCurrency(summary?.gain_loss || 0)}
                  </span>
                </div>
                {summary?.annualized_return != null && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      <span className="text-sm">{t("dash.annReturn")}<InfoTip text="Annualized return weighted by cost basis. Calculated per asset: (gain / cost) × (365 / days held), then averaged." /></span>
                    </div>
                    <span className={`font-mono font-semibold ${summary.annualized_return >= 0 ? "text-green-500" : "text-red-500"}`}>
                      {summary.annualized_return >= 0 ? "+" : ""}{summary.annualized_return.toFixed(1)}%
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <span className="text-sm">{t("dash.creditPayments")}<InfoTip text="Sum of monthly payments across all active credits/loans." /></span>
                  </div>
                  <span className="font-mono font-semibold text-red-400">
                    -{formatCurrency(summary?.monthly_credit_payments || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-primary" />
                    <span className="text-sm">{t("dash.assetsCredits")}<InfoTip text="Total number of tracked assets and credit/loan positions." /></span>
                  </div>
                  <span className="font-mono font-semibold">
                    {summary?.asset_count || 0} / {summary?.credit_count || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Historical Performance Chart */}
          <Card className="md:col-span-12" data-testid="historical-chart-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80">
                  {t("dash.historicalPerf")}
                </CardTitle>
                <div className="flex items-center gap-1 bg-secondary rounded-lg p-0.5">
                  {[
                    { key: "day", label: "30D" },
                    { key: "month", label: "12M" },
                    { key: "year", label: "All" },
                  ].map((opt) => (
                    <Button
                      key={opt.key}
                      variant={historyRange === opt.key ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setHistoryRange(opt.key)}
                      data-testid={`history-range-${opt.key}`}
                      className={`text-xs h-7 px-3 ${historyRange === opt.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : historyData.length > 1 ? (
                <div className="h-64" data-testid="history-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historyData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="histGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(76, 91%, 60%)" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="hsl(76, 91%, 60%)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="histNWGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toFixed(0)}
                        width={55}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                        formatter={(value, name) => [
                          `$${Math.round(value).toLocaleString()}`,
                          name === "value" ? "Portfolio Value" : "Net Worth",
                        ]}
                        labelFormatter={(label) => label}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="hsl(76, 91%, 60%)"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#histGradient)"
                        name="value"
                      />
                      <Area
                        type="monotone"
                        dataKey="netWorth"
                        stroke="hsl(217, 91%, 60%)"
                        strokeWidth={1.5}
                        fillOpacity={1}
                        fill="url(#histNWGradient)"
                        name="netWorth"
                        strokeDasharray="4 2"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="flex items-center justify-center gap-6 mt-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-[hsl(76,91%,60%)]" />
                      <span className="text-xs text-muted-foreground">{t("dash.portfolioValue")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-[hsl(217,91%,60%)] opacity-70" style={{ borderTop: "1.5px dashed hsl(217, 91%, 60%)" }} />
                      <span className="text-xs text-muted-foreground">{t("dash.netWorth")}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <BarChart3 className="w-10 h-10 mb-3 opacity-40" />
                  <p className="font-medium">{t("dash.notEnoughData")}</p>
                  <p className="text-sm mt-1">{t("dash.notEnoughDataSub")}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Assets */}
          <Card className="md:col-span-12" data-testid="recent-assets-card">
            <CardHeader>
              <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80">
                {t("dash.recentAssets")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {assets.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {assets.slice(0, 4).map((asset) => (
                    <div
                      key={asset.asset_id}
                      className="p-4 bg-secondary/50 rounded-lg border border-border"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold truncate">{asset.name}</p>
                          <p className="text-xs text-muted-foreground uppercase">
                            {asset.asset_type} {asset.symbol && `• ${asset.symbol}`}
                          </p>
                        </div>
                        <RiskBadge score={asset.risk_score} />
                      </div>
                      <p className="text-xl font-mono font-bold mt-3">
                        {formatCurrency((asset.current_price || asset.purchase_price) * asset.quantity)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {asset.quantity} units @ {formatCurrency(asset.current_price || asset.purchase_price)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    {t("dash.noAssetsShort")}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Wealth Advisor Dialog */}
        <Dialog open={isAdvisorOpen} onOpenChange={setIsAdvisorOpen}>
          <DialogContent className="max-w-xl h-[70vh] flex flex-col p-0 gap-0">
            <div className="flex items-center gap-3 px-5 py-4 border-b bg-secondary/30">
              <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm font-['Barlow_Condensed'] uppercase tracking-wide">{t("dash.advisor")}</p>
                <p className="text-xs text-muted-foreground">{t("dash.advisorSub")}</p>
              </div>
            </div>
            
            <ScrollArea className="flex-1 px-5">
              <div className="space-y-3 py-4">
                {chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-2.5 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                    data-testid={`advisor-msg-${idx}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      msg.role === "user" ? "bg-primary" : "bg-secondary"
                    }`}>
                      {msg.role === "user" ? (
                        <User className="w-3.5 h-3.5 text-primary-foreground" />
                      ) : (
                        <Bot className="w-3.5 h-3.5 text-primary" />
                      )}
                    </div>
                    <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl ${
                      msg.role === "user" 
                        ? "bg-primary text-primary-foreground rounded-br-sm" 
                        : "bg-secondary rounded-bl-sm"
                    }`}>
                      {msg.loading ? (
                        <div className="flex items-center gap-2 py-1">
                          <span className="flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
                          </span>
                          <span className="text-xs text-muted-foreground">{t("dash.analyzing")}</span>
                        </div>
                      ) : (
                        <div className="text-sm leading-relaxed advisor-prose">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                              strong: ({ children }) => <span className="font-semibold">{children}</span>,
                              ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                              li: ({ children }) => <li className="text-sm">{children}</li>,
                              h1: ({ children }) => <p className="font-semibold text-sm mb-1">{children}</p>,
                              h2: ({ children }) => <p className="font-semibold text-sm mb-1">{children}</p>,
                              h3: ({ children }) => <p className="font-semibold text-sm mb-1">{children}</p>,
                              code: ({ children }) => <code className="bg-background/50 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>
            
            <div className="flex gap-2 px-5 py-3 border-t bg-background">
              <Input
                placeholder={t("dash.askPortfolio")}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                disabled={advisorLoading}
                className="text-sm"
                data-testid="advisor-input"
              />
              <Button 
                onClick={sendMessage} 
                disabled={advisorLoading || !userInput.trim()}
                size="icon"
                className="bg-primary text-primary-foreground shrink-0"
                data-testid="advisor-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
